import React, { useState, useEffect } from 'react';
import {
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    useTheme,
    Text,
    IconButton,
} from '@fluentui/react';
import { ComponentDetailsResponse } from '../models/CloudStatusApiModels';
import { getComponentDetails } from '../api/SDSApi';
import { ComponentDetailsTab } from './ComponentDetailsTab';
import { ComponentEventsTab } from './ComponentEventsTab';
import '../styles/CommonStyles.css';

interface ComponentDetailsProps {
    selectedComponentId: string | null;
}

export const ComponentDetails: React.FC<ComponentDetailsProps> = ({
    selectedComponentId,
}) => {
    const theme = useTheme();
    const [componentDetails, setComponentDetails] =
        useState<ComponentDetailsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState<string>('details');
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        const fetchDetails = async () => {
            if (!selectedComponentId) {
                if (!isCancelled) {
                    setComponentDetails(null);
                }
                return;
            }

            if (!isCancelled) {
                setIsLoading(true);
            }

            try {
                const response = await getComponentDetails(
                    selectedComponentId,
                    true,
                    true
                );

                if (!isCancelled) {
                    if (response.success) {
                        setComponentDetails(response);
                    } else {
                        console.error(
                            'Failed to fetch component details:',
                            response.errorMessage
                        );
                        setComponentDetails(null);
                    }
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching component details:', error);
                if (!isCancelled) {
                    setComponentDetails(null);
                    setIsLoading(false);
                }
            }
        };

        fetchDetails();

        return () => {
            isCancelled = true;
        };
    }, [selectedComponentId]);

    // Show empty state when no component is selected
    if (!selectedComponentId) {
        return (
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                    backgroundColor: theme.palette.neutralLighter,
                    borderRadius: '4px',
                }}
            >
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            textAlign: 'center',
                        },
                    }}
                >
                    Select a component to view its details
                </Text>
            </div>
        );
    }

    // Show loading state
    if (isLoading) {
        return (
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                    boxShadow:
                        '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                }}
            >
                <Spinner
                    size={SpinnerSize.large}
                    label="Loading component details..."
                />
            </div>
        );
    }

    // Show error state
    if (!componentDetails) {
        return (
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                    backgroundColor: theme.palette.neutralLighter,
                    borderRadius: '4px',
                }}
            >
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            textAlign: 'center',
                        },
                    }}
                >
                    Failed to load component details
                </Text>
            </div>
        );
    }

    return (
        <div
            style={{
                backgroundColor: theme.palette.white,
                borderRadius: '4px',
                boxShadow:
                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                overflow: 'hidden',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header with collapse button */}
            <div
                style={{
                    padding: '16px 20px',
                    backgroundColor: theme.palette.neutralQuaternary,
                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <Text
                        variant="xLarge"
                        styles={{
                            root: {
                                fontWeight: 700,
                                color: theme.palette.black,
                            },
                        }}
                    >
                        {componentDetails.componentName}
                    </Text>
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralSecondary,
                                marginTop: '4px',
                                display: 'block',
                            },
                        }}
                    >
                        {componentDetails.serviceCategory} |{' '}
                        {componentDetails.pipelineStage}
                    </Text>
                </div>
                <IconButton
                    iconProps={{
                        iconName: isCollapsed ? 'ChevronDown' : 'ChevronUp',
                    }}
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                    ariaLabel={
                        isCollapsed
                            ? 'Expand component details'
                            : 'Collapse component details'
                    }
                    aria-expanded={!isCollapsed}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    styles={{
                        root: {
                            color: theme.palette.themePrimary,
                        },
                    }}
                />
            </div>

            {/* Tabs - Only show when not collapsed */}
            {!isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Pivot
                        aria-label="Component Details Tabs"
                        selectedKey={selectedTab}
                        onLinkClick={(item) => {
                            if (item?.props.itemKey) {
                                setSelectedTab(item.props.itemKey);
                            }
                        }}
                        styles={{
                            root: {
                                padding: '0 20px',
                                backgroundColor:
                                    theme.palette.neutralLighterAlt,
                            },
                        }}
                    >
                        <PivotItem
                            headerText="Details"
                            itemKey="details"
                            itemIcon="Info"
                        >
                            <div style={{ padding: '20px' }}>
                                <ComponentDetailsTab
                                    componentDetails={componentDetails}
                                />
                            </div>
                        </PivotItem>

                        <PivotItem
                            headerText="Events"
                            itemKey="events"
                            itemIcon="History"
                        >
                            <div>
                                <ComponentEventsTab
                                    selectedComponentId={selectedComponentId}
                                />
                            </div>
                        </PivotItem>
                    </Pivot>
                </div>
            )}
        </div>
    );
};
